// Krug 5 (K16.2, docs/KRUG5_K16_novi_igrac.md): systems open when they become useful. One table for the
// server and the client: the system and its condition, read from the survivor's state (level, runs,
// stacks, the Collection book), so a survivor who already meets a condition sees everything as before.
// While a system is closed the game uses its default (50% retreat, empty trade lists, no Shadow), so
// nothing breaks and nothing is blocked. Once open, a system stays open (`users.flags.unlocks`).
// Systems that already open on their own (attribute points, Ammo and Fuel, the Trader at level 3,
// contracts after the second run) keep their rules; the ones below are the ones K16.2 adds or joins.

import { unlockedOutfits } from './outfits';
import { SKILL_NODES, skillPathCost } from './skills/tree';
import type { CollectionState } from './collection';
import type { StackMap } from './types';
import { LEDGER_RULES } from './ledger';
import { SCHEDULED_FEATURES, SCHEDULE_RULES, scheduleOpensAt } from './schedule';
import { GENERATOR } from './fuelUses';
import { CREW_RULES, crewJoinOpen } from './crews';
import { PIT_RULES } from './pit';
import { MARKET_RULES } from './market';

export type FeatureId =
  | 'shadow'
  | 'retreat'
  | 'tradeLists'
  | 'painkillers'
  | 'stim'
  | 'medkit'
  | 'skills'
  | 'respec'
  | 'upgrade'
  | 'dismantleBulk'
  | 'relicForge'
  | 'appearance'
  | 'contracts'
  | 'season'
  | 'ads'
  | 'hordeJoin'
  | 'ledger'
  // Package 4, part A (7. 10. 2026): systems the schedule opens for a new account; open for everyone else as before.
  | 'craft'
  | 'meals'
  | 'buildings'
  | 'trader'
  | 'collection'
  // 10. 10. 2026 (owner, deploy7): by level on a schedule too (the truck and the generator 4, the Molotov 6, the radio 7).
  | 'truck'
  | 'generator'
  | 'molotov'
  | 'radio'
  // 11. 10. 2026 (owner, BACKLOG #33): crews, from level 8 or after the account's first week (founding from level 10).
  | 'crews'
  // 11. 10. 2026 (owner, docs/thepit.md): The Pit from level 10, while the server's FEATURE_PIT is on.
  | 'pit'
  // 11. 10. 2026 (owner, docs/marketplace.md): the Market, level 10 with an account older than 7 days, behind its flag.
  | 'market';

/** What the conditions read. `runsDone`: the board rule (onboarding over or two runs back). */
export interface UnlockInput {
  level: number;
  runsDone: boolean;
  /** Storage and pack together. */
  stacks: StackMap;
  /** Outfits unlocked in the Collection book. */
  outfits: number;
  /** 4. 10. 2026 (package 3, A): Early Skills opens the skill tree at level 4 (missing: 5). */
  treeAt?: number;
  /**
   * Package 4, part A: an account with a schedule (made after the deploy) opens the systems of the schedule by it alone
   * (the features of the rows opened so far); missing: the rules by level.
   */
  schedule?: { opened: FeatureId[] } | null;
  /** 11. 10. 2026: the Market's flag on this server. */
  market?: boolean;
  /** deploy8: the crews' flag (`groups`) on this server; missing = on. Off on the server (owner 11. 10. 2026). */
  groups?: boolean;
  /** 11. 10. 2026 (owner): the ACCOUNT's age in whole days (`users.created_at`), for the crews after the first week and the Market. */
  accountDays?: number;
}

export interface FeatureUnlock {
  id: FeatureId;
  /** The short name in a toast with several (`New at level 5: Shadow stance, appearance`). */
  name: string;
  /** The toast when it opens alone; none for systems announced another way (their card, the skill point). */
  toast?: { title: string; body: string };
  /** Where the toast's button goes. */
  to?: string;
  /** The level it opens at, when that is the rule (`Unlocks at Lv 2`, `Join from Lv 3`). */
  level?: number;
  /** 11. 10. 2026 (BACKLOG #33): it also opens after this many days of the account, on a schedule too (crews: 7). */
  orDays?: number;
  open: (u: UnlockInput) => boolean;
}

const has = (u: UnlockInput, k: keyof StackMap) => (u.stacks[k] ?? 0) > 0;

export const FEATURE_UNLOCKS: FeatureUnlock[] = [
  { id: 'retreat', name: 'retreat threshold', level: 3, toast: { title: 'New: retreat threshold.', body: 'Choose when your survivor turns back.' }, to: '/map', open: (u) => u.level >= 3 },
  { id: 'upgrade', name: 'item upgrades', level: 3, toast: { title: 'New: item upgrades.', body: 'Make your best gear stronger at the Workshop.' }, to: '/inventory', open: (u) => u.level >= 3 },
  { id: 'dismantleBulk', name: 'dismantle many', level: 3, toast: { title: 'New: dismantle many.', body: 'Clear out old gear in one go.' }, to: '/inventory', open: (u) => u.level >= 3 },
  // 9. 10. 2026 (owner): the horde from level 5 (was 3), also on the schedule (its row's `minLevel`).
  { id: 'hordeJoin', name: 'the horde', level: 5, toast: { title: 'New: the horde.', body: 'Join the evening fight with everyone else.' }, to: '/horde', open: (u) => u.level >= 5 },
  { id: 'tradeLists', name: 'trade lists', level: 4, toast: { title: 'New: trade lists.', body: 'Say what you offer and what you want when you meet someone.' }, to: '/map', open: (u) => u.level >= 4 },
  { id: 'shadow', name: 'Shadow stance', level: 5, toast: { title: 'New: Shadow stance.', body: 'Follow others and strike from hiding.' }, to: '/map', open: (u) => u.level >= 5 },
  // 29. 9. 2026: respec and the tree reset from level 6 (the tree itself opens at 5).
  { id: 'respec', name: 'respec', level: 6, toast: { title: 'New: respec.', body: 'Move attribute points and reset the skill tree.' }, to: '/survivor', open: (u) => u.level >= 6 },
  { id: 'appearance', name: 'appearance', level: 5, toast: { title: 'New: appearance.', body: 'Wear the outfits you unlock.' }, to: '/survivor/appearance', open: (u) => u.level >= 5 || u.outfits > 0 },
  // 29. 9. 2026: the tree opens at level 5 (points still come from level 2 and are kept; at 5 there are
  // 4, what the first keystone costs). Its own toast with the points (`skillTreeToast`), not in a list.
  { id: 'skills', name: 'skill tree', level: 5, open: (u) => u.level >= (u.treeAt ?? 5) },
  { id: 'painkillers', name: 'Painkillers', toast: { title: 'New: Painkillers in your supplies.', body: 'Take them on a run.' }, to: '/map', open: (u) => has(u, 'painkillers') },
  { id: 'stim', name: 'Stim', toast: { title: 'New: Stim in your supplies.', body: 'Take it on a run.' }, to: '/map', open: (u) => has(u, 'stim') },
  { id: 'medkit', name: 'Medkit', toast: { title: 'New: Medkit in your supplies.', body: 'Take it on a run.' }, to: '/map', open: (u) => has(u, 'medkit') },
  { id: 'relicForge', name: 'Relic Forge', toast: { title: 'New: Relic Forge.', body: 'Turn Relic Shards into a Legendary at the Workshop.' }, to: '/workshop?tab=relic', open: (u) => has(u, 'relic_shard') },
  // 4. 10. 2026 (package 3, G): the Veteran Ledger on level 20 (Survivor → Ledger).
  { id: 'ledger', name: 'Veteran Ledger', level: 20, toast: { title: 'New: the Veteran Ledger.', body: 'Ranks, achievements and book milestones give Marks. Spend them on camp upgrades for this season.' }, to: '/survivor/ledger', open: (u) => u.level >= 20 && LEDGER_RULES.on },
  // With the contract board (their cards say it themselves, as before).
  { id: 'contracts', name: 'contracts', open: (u) => u.runsDone },
  { id: 'season', name: 'season path', open: (u) => u.runsDone },
  { id: 'ads', name: 'ads', open: (u) => u.runsDone },
  // Package 4, part A: open from the start for an account without a schedule (no toast, no New mark: the schedule's
  // card or guided step says it); the Trader at level 3 as before (its own message).
  { id: 'craft', name: 'crafting', open: () => true },
  { id: 'meals', name: 'meals', open: () => true },
  { id: 'buildings', name: 'building upgrades', open: () => true },
  { id: 'trader', name: 'the Trader', level: 3, open: (u) => u.level >= 3 },
  { id: 'collection', name: 'the Collection book', open: () => true },
  // 10. 10. 2026: without a schedule as before (the generator from its level, the rest from the start).
  { id: 'truck', name: 'the truck', open: () => true },
  { id: 'generator', name: 'the generator', level: GENERATOR.unlockLevel, open: (u) => u.level >= GENERATOR.unlockLevel },
  { id: 'molotov', name: 'Molotov', open: () => true },
  { id: 'radio', name: 'the radio', open: () => true },
  // 11. 10. 2026 (owner, BACKLOG #33): crews from level 8 or after the account's first week, whichever comes first.
  {
    id: 'crews',
    name: 'crews',
    level: CREW_RULES.joinLevel,
    orDays: CREW_RULES.joinAfterDays,
    toast: { title: 'New: crews.', body: 'Join a crew of up to five survivors, or found one from level 10.' },
    to: '/crew',
    open: (u) => u.groups !== false && crewJoinOpen(u.level, u.accountDays ?? 0),
  },
  // 11. 10. 2026 (owner, docs/thepit.md): the arena at level 10; nothing of it while the server's flag is off.
  { id: 'pit', name: 'The Pit', level: PIT_RULES.openLevel, toast: { title: 'New: The Pit.', body: 'Fight other survivors at the Steelworks. You lose no gear there.' }, to: '/pit', open: (u) => u.level >= PIT_RULES.openLevel && PIT_RULES.on },
  // 11. 10. 2026 (owner): the Market, behind its flag (off until about 50 players a day); its own toast without a schedule.
  {
    id: 'market',
    name: 'the Market',
    level: MARKET_RULES.level,
    toast: { title: 'New: the Market.', body: 'Buy and sell gear with other survivors for Relic Shards.' },
    to: '/market',
    open: (u) => !!u.market && u.level >= MARKET_RULES.level && (u.accountDays ?? 0) >= MARKET_RULES.accountDays,
  },
];

export const featureUnlock = (id: FeatureId) => FEATURE_UNLOCKS.find((f) => f.id === id)!;

/**
 * 10. 10. 2026 (owner, deploy7): the level a system opens at for a new account (the schedule's, by level only), else
 * its own rule's level; null for one that opens another way (a stack, the first runs).
 */
export function featureOpenLevel(id: FeatureId): number | null {
  return (SCHEDULE_RULES.on ? scheduleOpensAt(id)?.level : undefined) ?? FEATURE_UNLOCKS.find((f) => f.id === id)?.level ?? null;
}

/**
 * Package 4, part A: systems that only a schedule opens. An account without one has them by their old rule (from the
 * start, the Trader at level 3) and they are never written down or counted as opened for it.
 */
export const SCHEDULE_ONLY_FEATURES: readonly FeatureId[] = ['craft', 'meals', 'buildings', 'trader', 'truck', 'generator', 'molotov', 'radio'];

/**
 * 13. 10. 2026 (owner): systems open from the start for every account, on a schedule or not; never written down or
 * counted as opened (no toast, no New, no `feature_unlocked`). The Collection book left the schedule (it was level 7).
 */
export const ALWAYS_OPEN_FEATURES: readonly FeatureId[] = ['collection'];

/**
 * Whether one system is open now. With a schedule (package 4, part A) its systems open by it alone (Appearance also with
 * the first outfit, as before); the rest keep their rule.
 */
export function featureIsOpen(f: FeatureUnlock, u: UnlockInput): boolean {
  if (u.schedule && SCHEDULED_FEATURES.has(f.id)) return u.schedule.opened.includes(f.id) || (f.id === 'appearance' && u.outfits > 0) || (f.orDays != null && (u.accountDays ?? 0) >= f.orDays);
  return f.open(u);
}

/** The systems whose condition holds now. */
export function openFeatures(u: UnlockInput): FeatureId[] {
  return FEATURE_UNLOCKS.filter((f) => featureIsOpen(f, u)).map((f) => f.id);
}

/**
 * The toast for systems that opened together: one sentence for one, a list for several
 * (`New at level 5: Shadow stance, appearance`). Null when none of them has a toast.
 */
export function unlockToast(ids: FeatureId[], level: number): { title: string; body: string; to: string } | null {
  const shown = FEATURE_UNLOCKS.filter((f) => ids.includes(f.id) && f.toast);
  if (!shown.length) return null;
  if (shown.length === 1) return { ...shown[0].toast!, to: shown[0].to ?? '/camp' };
  const atLevel = shown.every((f) => f.level === level);
  return { title: `${atLevel ? `New at level ${level}` : 'New'}: ${shown.map((f) => f.name).join(', ')}`, body: 'Each one has a New mark until you use it.', to: shown[0].to ?? '/camp' };
}

/** The level the skill tree opens at (points come from level 2 and wait). */
export const SKILL_TREE_LEVEL = FEATURE_UNLOCKS.find((f) => f.id === 'skills')!.level!;

/** What the nearest keystone costs from a background's start. */
export function firstKeystoneCost(background: string): number {
  const costs = Object.values(SKILL_NODES)
    .filter((n) => n.type === 'keystone')
    .map((n) => skillPathCost(background, n.id))
    .filter((c): c is number => c != null);
  return costs.length ? Math.min(...costs) : 4;
}

/** The toast when the tree opens (29. 9. 2026): the points waiting, and whether they reach a keystone. */
export function skillTreeToast(points: number, background: string): { title: string; body: string; to: string } {
  const pts = points === 1 ? '1 point' : `${points} points`;
  return {
    title: 'New: skill tree.',
    body: points >= firstKeystoneCost(background) ? `You have ${pts}: enough for your first keystone.` : `You have ${pts} to spend.`,
    to: '/skills',
  };
}

/** Systems that show a `New` mark until first used (the ones with a toast). */
export const MARKED_FEATURES: FeatureId[] = FEATURE_UNLOCKS.filter((f) => f.toast).map((f) => f.id);

/** Outfits unlocked in the book (the Appearance condition). */
export const unlockOutfitsCount = (c: CollectionState) => unlockedOutfits(c).length;
